import React, { useState } from 'react';
import { bool, node } from 'prop-types';
import { compose } from 'redux';
import { Form as FinalForm } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import classNames from 'classnames';

import { FormattedMessage, injectIntl, intlShape } from '../../../util/reactIntl';
import * as validators from '../../../util/validators';
import { SaveUnfinishedSignup } from '../../../util/api';
import {
  Form,
  PrimaryButton,
  FieldTextInput,
  FieldPhoneNumberInput,
  CustomExtendedDataField,
  // MultiAssetFocus,
  FieldSelect,
  FieldToggleGroup,
  FieldSwitchesGroup,
} from '../../../components';

import css from './SignupForm.module.css';
import { getBirthDates, getBirthMonths, getBirthYears, getPropsForCustomUserFieldInputs } from '../../../util/userHelpers';

const SignupFormComponent = props => {
  const [tab, setTab] = useState(1);

  return <FinalForm
    {...props}
    mutators={{ ...arrayMutators }}
    render={fieldRenderProps => {
      const {
        rootClassName,
        className,
        formId,
        handleSubmit,
        inProgress,
        invalid,
        intl,
        termsAndConditions,
        userFields,
        values,
        submitFailed,
        categories,
        membershipId,
      } = fieldRenderProps;

      // email
      const emailRequired = validators.required(
        intl.formatMessage({
          id: 'SignupForm.emailRequired',
        })
      );
      const emailValid = validators.emailFormatValid(
        intl.formatMessage({
          id: 'SignupForm.emailInvalid',
        })
      );

      // password
      const passwordRequiredMessage = intl.formatMessage({
        id: 'SignupForm.passwordRequired',
      });
      const passwordMinLengthMessage = intl.formatMessage(
        {
          id: 'SignupForm.passwordTooShort',
        },
        {
          minLength: validators.PASSWORD_MIN_LENGTH,
        }
      );
      const passwordMaxLengthMessage = intl.formatMessage(
        {
          id: 'SignupForm.passwordTooLong',
        },
        {
          maxLength: validators.PASSWORD_MAX_LENGTH,
        }
      );
      const passwordMinLength = validators.minLength(
        passwordMinLengthMessage,
        validators.PASSWORD_MIN_LENGTH
      );
      const passwordMaxLength = validators.maxLength(
        passwordMaxLengthMessage,
        validators.PASSWORD_MAX_LENGTH
      );
      const passwordRequired = validators.requiredStringNoTrim(passwordRequiredMessage);
      const passwordValidators = validators.composeValidators(
        passwordRequired,
        passwordMinLength,
        passwordMaxLength
      );

      const passwordsMatch = validators.isEqual(
        "Passwords don't match.",
        values.password,
      );
      const confirmPasswordValidators = validators.composeValidators(
        passwordRequired,
        passwordsMatch,
      );

      const telephone_contactLabel = intl.formatMessage({
        id: 'ProfileSettingsForm.telephone_contactLabel',
      });
      const telephone_contactPlaceholder = intl.formatMessage({
        id: 'ProfileSettingsForm.telephone_contactPlaceholder',
      });
      const telephone_contactRequiredMessage = intl.formatMessage({
        id: 'ProfileSettingsForm.telephone_contactRequired',
      });
      const telephone_contactRequired = validators.required(telephone_contactRequiredMessage);

      // Custom user fields. Since user types are not supported here,
      // only fields with no user type id limitation are selected.
      const userFieldProps = getPropsForCustomUserFieldInputs(userFields, intl);

      const classes = classNames(rootClassName || css.root, className);
      const submitInProgress = inProgress;

      // const hasAtLeastOneMainFocus = values.mainFocus && values.mainFocus.length > 0;
      const submitDisabled = submitInProgress
        // || !hasAtLeastOneMainFocus
        || invalid;

      return (
        <Form className={classes} onSubmit={handleSubmit}>
          {tab == 1 ? <div>
            <div className={css.name}>
              <FieldTextInput
                className={css.firstNameRoot}
                type="text"
                id={formId ? `${formId}.fname` : 'fname'}
                name="fname"
                autoComplete="given-name"
                label={intl.formatMessage({
                  id: 'SignupForm.firstNameLabel',
                })}
                placeholder={intl.formatMessage({
                  id: 'SignupForm.firstNamePlaceholder',
                })}
                validate={validators.required(
                  intl.formatMessage({
                    id: 'SignupForm.firstNameRequired',
                  })
                )}
              />
              <FieldTextInput
                className={css.lastNameRoot}
                type="text"
                id={formId ? `${formId}.lname` : 'lname'}
                name="lname"
                autoComplete="family-name"
                label={intl.formatMessage({
                  id: 'SignupForm.lastNameLabel',
                })}
                placeholder={intl.formatMessage({
                  id: 'SignupForm.lastNamePlaceholder',
                })}
                validate={validators.required(
                  intl.formatMessage({
                    id: 'SignupForm.lastNameRequired',
                  })
                )}
              />
            </div>

            <FieldTextInput
              type="email"
              id={formId ? `${formId}.email` : 'email'}
              name="email"
              autoComplete="email"
              label={intl.formatMessage({
                id: 'SignupForm.emailLabel',
              })}
              placeholder={intl.formatMessage({
                id: 'SignupForm.emailPlaceholder',
              })}
              validate={validators.composeValidators(emailRequired, emailValid)}
            />

            <FieldPhoneNumberInput
              // className={css.additionalInputs}
              id="telephone_contact"
              name="telephone_contact"
              label={telephone_contactLabel}
              placeholder={telephone_contactPlaceholder}
              validate={telephone_contactRequired}
            />

            <FieldTextInput
              className={css.password}
              type="password"
              id={formId ? `${formId}.password` : 'password'}
              name="password"
              autoComplete="new-password"
              label={intl.formatMessage({
                id: 'SignupForm.passwordLabel',
              })}
              placeholder={intl.formatMessage({
                id: 'SignupForm.passwordPlaceholder',
              })}
              validate={passwordValidators}
            />
            <FieldTextInput
              className={css.password}
              type="password"
              id={formId ? `${formId}.confirmPassword` : 'confirmPassword'}
              name="confirmPassword"
              label="Confirm Password"
              placeholder={intl.formatMessage({
                id: 'SignupForm.passwordPlaceholder',
              })}
              validate={confirmPasswordValidators}
            />

            <div className={css.contactMethodLabel}>
              <span>Preferred contact method for:</span>
              <span>a.) Deal related message from another user</span>
              <span>b.) New deal summary</span>
            </div>
            <FieldSwitchesGroup
              className={css.contactMethod}
              id="contactmethod"
              name="contactmethod"
              label=""
              options={[{
                key: "whatsapp",
                label: "Whatsapp",
              }, {
                key: "email",
                label: "Email",
              }]}
              validate={validators.requiredFieldArrayCheckbox("Required")}
            />
            <p className={css.junkWarning}>
              (Email can go to junk. Whatsapp highly recommended for deliverability, and speed of communication)
            </p>
          </div> : null}

          {tab == 2 ? <div>
            <div className={css.interestHeader}>
              Select your interests
            </div>
            <FieldToggleGroup
              className={css.mainFocus}
              id="mainFocus"
              name="mainFocus"
              label=""
              options={categories.map(c => {
                return {
                  key: c.id,
                  label: c.name,
                };
              })}
              validate={validators.requiredFieldArrayCheckbox("Required")}
            />

            <div className={css.interestHeader}>
              {intl.formatMessage({
                id: 'ProfileSettingsForm.primaryAssetClass',
              })}
            </div>
            <FieldSelect
              id="primaryAssetClass"
              name="primaryAssetClass"
              label=""
              validate={validators.required("Required")}
            >
              <option value="">Select one</option>
              {categories.map(c => <option key={c.id} value={c.id}>
                {c.name}
              </option>)}
            </FieldSelect>
          </div> : null}

          {tab == 3 ? <div>
            <div className={css.birthNote}>
              Select your birth year for KYC
            </div>
            <FieldSelect
              className={css.additionalInputs}
              type="number"
              id="birthYear"
              name="birthYear"
              label={intl.formatMessage({
                id: 'ProfileSettingsForm.birthYearLabel',
              })}
              validate={validators.required(intl.formatMessage({
                id: 'ProfileSettingsForm.birthYearRequired',
              }))}
            >
              <option value="">{intl.formatMessage({
                id: 'ProfileSettingsForm.birthYearPlaceholder',
              })}</option>
              {getBirthYears().map(year => <option key={year}>{year}</option>)}
            </FieldSelect>

            {values.birthYear && <FieldSelect
              className={css.additionalInputs}
              type="number"
              id="birthMonth"
              name="birthMonth"
              label={intl.formatMessage({
                id: 'ProfileSettingsForm.birthMonthLabel',
              })}
              validate={validators.required(intl.formatMessage({
                id: 'ProfileSettingsForm.birthMonthRequired',
              }))}
            >
              <option value="">{intl.formatMessage({
                id: 'ProfileSettingsForm.birthMonthPlaceholder',
              })}</option>
              {getBirthMonths().map(({ value, display }) => <option key={value} value={value + 1}>{display}</option>)}
            </FieldSelect>}

            {values.birthMonth && <FieldSelect
              className={css.additionalInputs}
              type="number"
              id="birthDate"
              name="birthDate"
              label={intl.formatMessage({
                id: 'ProfileSettingsForm.birthDateLabel',
              })}
              validate={validators.required(intl.formatMessage({
                id: 'ProfileSettingsForm.birthDateRequired',
              }))}
            >
              <option value="">{intl.formatMessage({
                id: 'ProfileSettingsForm.birthDatePlaceholder',
              })}</option>
              {getBirthDates(values.birthMonth, values.birthYear).map(date => <option key={date}>{date}</option>)}
            </FieldSelect>}

            {userFieldProps.map(fieldProps => (
              <CustomExtendedDataField
                {...fieldProps}
                formId={formId}
                values={values}
              />
            ))}

            <div>
              <FieldSwitchesGroup
                className={css.notifyMe}
                id="notifyMe"
                name="notifyMe"
                options={[{
                  key: "listingsAndMessages",
                  label: "Notify me of listings and direct messages, bespoke to my preferences",
                }]}
                validate={validators.requiredFieldArrayCheckbox("Required")}
              />
              {termsAndConditions}
            </div>
          </div> : null}

          {/* <div className={css.assetsFocusWrapper}>
            <MultiAssetFocus
              values={values}
              intl={intl}
            />
          </div> */}

          <div className={css.footer}>
            {tab !== 1 ? <PrimaryButton
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setTab(tab - 1);

                if (typeof window !== "undefined") {
                  window.scrollTo(0, 0);
                };
              }}
              className={css.submitButton}
            >
              <FormattedMessage id="SignupForm.back" />
            </PrimaryButton> : <div />}

            <div className={css.counterButtonWrapper}>
              <div className={css.stepper}>
                {tab} of 3
              </div>

              {tab == 3 ? <PrimaryButton
                type="submit"
                inProgress={submitInProgress}
                disabled={submitDisabled}
                className={css.submitButton}
              >
                <FormattedMessage id="SignupForm.signUp" />
              </PrimaryButton> : <PrimaryButton
                type="button"
                disabled={submitDisabled}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (tab == 1) {
                    try {
                      SaveUnfinishedSignup({
                        firstname: values.fname,
                        lastname: values.lname,
                        email: values.email,
                        countryCode: values.telephone_country_code,
                        phoneNumber: values.telephone_contact,
                        preferredContactMethod: values.contactmethod,
                        membershipId,
                      });
                    } catch (error) {
                      console.error(error);
                    };
                  };

                  setTab(tab + 1);

                  if (typeof window !== "undefined") {
                    window.scrollTo(0, 0);
                  };
                }}
                className={css.submitButton}
              >
                <FormattedMessage id="SignupForm.next" />
              </PrimaryButton>}
            </div>
          </div>

          {submitFailed && <div className={css.submitError}>
            <FormattedMessage id="SignUpForm.submitFailed" />
          </div>}
        </Form>
      );
    }}
  />
};

SignupFormComponent.defaultProps = { inProgress: false };

SignupFormComponent.propTypes = {
  inProgress: bool,
  termsAndConditions: node.isRequired,

  // from injectIntl
  intl: intlShape.isRequired,
};

const SignupForm = compose(injectIntl)(SignupFormComponent);
SignupForm.displayName = 'SignupForm';

export default SignupForm;
